/**
 * カラオケ風の「再生中の文面」表示のロジック — Issue #643。
 *
 * 音声プレビューで、今どこを読み上げているかを文面上で追えるようにする。
 * Polly の speech marks を使わず、「再生時間の割合 = 文字数の割合」として近似する。
 * (サーバ側の変更や追加の API 呼び出しを必要としない。)
 */

const ENTITIES: Record<string, string> = {
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&apos;': "'",
  '&amp;': '&',
};

/**
 * SSML (またはプレーンテキスト) から、画面に出す読み上げ文面を取り出す。
 *
 * - タグは除去する (`<sub alias>` なども内側のテキストを残す)。
 * - エンティティ (`&amp;` 等) を復元する。
 * - 連続する空白・改行を 1 つのスペースにまとめ、前後を trim する。
 */
export function ssmlToPlainText(ssml: string | null | undefined): string {
  if (!ssml) return '';
  return ssml
    .replace(/<[^>]*>/g, '')
    .replace(/&(?:lt|gt|quot|apos|amp);/g, (entity) => ENTITIES[entity] ?? entity)
    .replace(/\s+/g, ' ')
    .trim();
}

/** 進捗を 0〜1 に丸める (NaN / 無限大は 0)。 */
export function clampProgress(ratio: number): number {
  if (!Number.isFinite(ratio)) return 0;
  return Math.min(1, Math.max(0, ratio));
}

/**
 * 文面を「読み上げ済み」と「これから」に分割する。
 *
 * サロゲートペアを壊さないよう `Array.from` (コードポイント単位) で数える。
 */
export function splitByProgress(text: string, ratio: number): { done: string; rest: string } {
  const chars = Array.from(text);
  const index = Math.round(chars.length * clampProgress(ratio));
  return { done: chars.slice(0, index).join(''), rest: chars.slice(index).join('') };
}
